import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { LanguagePill } from "@/components/LanguagePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`${t("welcome_back")}, ${u.name}`);
      navigate(u.onboarding_done ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen hero-bg flex flex-col">
      <div className="flex items-center justify-between px-6 py-4">
        <Logo /><LanguagePill />
      </div>
      <div className="flex flex-1 items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md rounded-3xl bg-white border border-slate-100 card-shadow-lg p-8">
          <h1 className="font-display text-3xl font-semibold text-navy text-center">{t("welcome_back")}</h1>
          <p className="text-center text-slate-500 mt-1 text-sm">{t("login_sub")}</p>

          <button data-testid="google-login-btn" onClick={googleLogin}
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-full border border-slate-200 py-3 font-medium text-navy hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            {t("continue_google")}
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> {t("or")} <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full rounded-full brand-gradient text-white hover:opacity-90 py-6">
              {loading ? t("loading") : t("sign_in")}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            {t("no_account")} <Link to="/register" className="font-semibold text-brandblue">{t("get_started")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
